/**
 * Step building blocks shared by every Submittals tour
 * (``list.ts``, ``detail.ts``, ``combined.ts``).
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

export const hasDetailMounted = (): boolean =>
  has('[data-tour-id="submittal-detail-header-actions"]');

// ─── List-side ───────────────────────────────────────────────────────────────

export const newSubmittalStep: TourStep = {
  id: 'new-submittal',
  target: '[data-tour-id="submittals-new"]',
  placement: 'left',
  title: 'Log a new submittal',
  body:
    'Click here to start a new submittal package — shop drawings, ' +
    'product data, samples, anything that needs review and approval.',
};

export const filtersStep: TourStep = {
  id: 'filters',
  target: '[data-tour-id="submittals-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Find what you need',
  body:
    'Search by number or title, and filter by status, priority, or ' +
    'package.',
};

export const listTableStep: TourStep = {
  id: 'table',
  target: '[data-tour-id="submittals-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open a submittal',
  body: 'Each row is one submittal revision. Click any row to open the full details.',
};

export const combinedTableStep: TourStep = {
  id: 'open-a-submittal',
  target: '[data-tour-id="submittals-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open a submittal',
  body: 'Click any row to open it. Press Next and we\u2019ll open the first one for you.',
  onBeforeNext: async () => {
    clickPreferReact('[data-tour-id="submittals-table"] tbody tr');
    await waitForCondition(hasDetailMounted, 4000, 'row click → detail page');
  },
  autoAdvanceWhen: () => hasDetailMounted(),
};

// ─── Detail-side ─────────────────────────────────────────────────────────────

export const headerActionsStep: TourStep = {
  id: 'header-actions',
  target: '[data-tour-id="submittal-detail-header-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Status & workflow',
  body:
    'Edit the submittal, open it from Draft, spin off a new revision, or ' +
    'export every submittal in the project as CSV.',
};

export const metadataStep: TourStep = {
  id: 'metadata',
  target: '[data-tour-id="submittal-detail-metadata"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Key fields',
  body:
    'Quick summary — priority, package, the current reviewer, the ' +
    'required-response date, and how many days the submittal has been open.',
};

export const workflowStep: TourStep = {
  id: 'workflow',
  target: '[data-tour-id="submittal-detail-workflow"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Workflow step',
  body:
    'The current review step plus its reviewers — pending vs. responded. ' +
    'Submit a reviewer\u2019s response (Approved, Revise & Resubmit, etc.) ' +
    'right from here.',
};

export const closeStep: TourStep = {
  id: 'close-distribute',
  target: '[data-tour-id="submittal-detail-close"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Close & distribute',
  body:
    'Set the final response code, pick distribution recipients, and close ' +
    'the submittal in one step once review is complete.',
};

export const attachmentsStep: TourStep = {
  id: 'attachments',
  target: '[data-tour-id="submittal-detail-attachments"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Attachments',
  body: 'Upload supporting drawings, specs, product data — anything reviewers need.',
};

export const revisionsStep: TourStep = {
  id: 'revisions',
  target: '[data-tour-id="submittal-detail-revisions"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Revisions',
  body:
    'Every revision of this submittal lives here. Click any row to jump ' +
    'between them.',
};
